import LoadImage from '@/components/LoadImage';
import TrackingService from '@/services/tracking';
import { getAsset } from '@/utils';
import { NewContentButton, HStack, Text, Box } from '@design-system/pc';
import React, { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import useVisitConsult from './hook/useVisitConsult';
import AgreeContent from './AgreeContent';
import FormContent from './FormContent';
import { useBraze } from '@/providers/BrazeProvider';
import _ from 'lodash';
import { format } from 'date-fns';
import { FloatingGroup } from '@/components/FloatingGroup';

interface VisitConsultProps {
  shopId: number;
}

const VisitConsult = ({ shopId }: VisitConsultProps) => {
  const {
    handleSubmitData,
    isValid,
    method,
    items,
    isKindListView,
    handleChangeTelInput,
    handlePopupDateTime,
    handleAgreement,
    handleChangeName,
    handleAddPictureList,
    handleRemovePictureFile,
    times,
    shopDetail,
    channelType,
    channelInfoData,
    isNameDisabled,
    isTelDisabled,
  } = useVisitConsult({ shopId });
  const { logCustomEvent } = useBraze();

  const isOpenhouse = shopDetail?.type?.code === 'S11';

  useEffect(() => {
    if (!shopDetail) return;

    logCustomEvent({
      eventName: 'bz_start_arrange_visit_store',
      eventProperties: {
        bz_date: format(new Date(), 'yyyy-MM-dd'),
        bz_store_type: _.get(shopDetail, 'type.name', 'no type'),
        bz_store_area: _.get(shopDetail, 'address.basic', 'no area'),
        bz_store_num: _.toString(_.get(shopDetail, 'id', 'no shopId')),
        bz_store_name: _.get(shopDetail, 'name', 'no name'),
      },
    });
  }, [logCustomEvent, shopDetail]);

  return (
    <Box
      {...TrackingService.tagGTM('visit_consult')}
      width="100%"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      mb="100px"
    >
      <FormProvider {...method}>
        <Box
          position="relative"
          as="form"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          mt="105px"
          onSubmit={method.handleSubmit(handleSubmitData)}
        >
          <Text
            variant="subTitle2"
            color="color_chip.primary"
            marginBottom="20px"
          >
            매장 상담 예약
          </Text>

          <Text
            variant="headline4"
            color="color_chip.primary"
            marginBottom="8px"
          >
            최대 할인 혜택과 공간 스타일링 제안을 받아보세요.
          </Text>

          <LoadImage
            style={{ marginBottom: '48px' }}
            width="100%"
            src={getAsset(
              '/assets/images/visit-consult/request/brand_values_banner.webp',
            )}
          />

          <Box
            width="570px"
            borderRadius="4px"
            mb="76px"
            border="1px solid"
            borderColor="color_chip.gray.100"
            backgroundColor="color_chip.gray.40"
          >
            <HStack space={20} padding={16} className="shop-info-wrapper">
              <Box width="80px" height="80px" display="flex">
                <LoadImage
                  src={shopDetail?.mainImages?.[0].url}
                  borderRadius="2px"
                />
              </Box>
              <Box display="flex" flexDirection="column">
                <Text variant="subTitle5" color="color_chip.gray.700">
                  {shopDetail?.name || ''}
                </Text>
                {!isOpenhouse && (
                  <Text variant="body1" color="color_chip.gray.600">
                    {shopDetail?.type?.name || ''}
                  </Text>
                )}
              </Box>
            </HStack>
            {channelInfoData?.title && (
              <HStack
                space={4}
                borderTop={'1px solid'}
                borderColor="color_chip.gray.100"
                padding={'8px 16px'}
                color="color_chip.gray.600"
              >
                <Text variant="body2">
                  [{channelInfoData.channelName}] {channelInfoData.title}
                </Text>
              </HStack>
            )}
          </Box>
          {isOpenhouse && (
            <Box width={170} position="absolute" top="103px" left="701px">
              <LoadImage
                src={getAsset(
                  '/assets/images/visit-consult/request/open_house_banner.png',
                )}
                borderRadius="2px"
              />
            </Box>
          )}
          <FormContent
            isNameDisabled={isNameDisabled}
            isTelDisabled={isTelDisabled}
            handlePopupDateTime={handlePopupDateTime}
            items={items}
            times={times}
            handleChangeTelInput={handleChangeTelInput}
            isKindListView={isKindListView}
            channelType={channelType}
            handleChangeName={handleChangeName}
            handleAddPictureList={handleAddPictureList}
            handleRemovePictureFile={handleRemovePictureFile}
          />
          <AgreeContent handleAgreement={handleAgreement} />
          <NewContentButton
            size={'lg'}
            emphasis="high"
            width="570px"
            type="submit"
            disabled={!isValid}
            {...TrackingService.tagGTM('visit_consult_reservation_button')}
          >
            예약하기
          </NewContentButton>
        </Box>
      </FormProvider>

      <FloatingGroup
        onlyTopButton={true}
        {...TrackingService.tagGTM('visit_consult_scroll_top_button')}
      />
    </Box>
  );
};

export default VisitConsult;
